export const DEFAULT_RESEARCH_MARKET_SORT = { key: '__ranking', direction: 'desc' };

function compareNullable(left, right) {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return 0;
}

function coerceFiniteNumber(value) {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function formatResearchMarketRulePercent(value) {
  if (value == null) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const percentValue = numeric * 100;
  const fixed = Math.abs(percentValue).toFixed(2);
  const display = fixed.replace(/^0(?=\.)/, '');
  return `${percentValue < 0 ? '-' : ''}${display}%`;
}

export function formatCompactPopulation(value) {
  if (value == null || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return '—';
  if (numeric >= 1000000) {
    const millions = Math.round((numeric / 1000000) * 10) / 10;
    return `${millions.toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  }
  if (numeric >= 1000) return `${Math.round(numeric / 1000)}K`;
  return Math.round(numeric).toLocaleString('en-US');
}

export function researchMarketPopulationTone(value) {
  if (value == null || value === '') return 'unavailable';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'unavailable';
  if (numeric < 25000) return 'low';
  if (numeric < 100000) return 'medium';
  return 'high';
}

export function researchMarketPopulationTitle(market) {
  if (market?.population == null || market.population === '') return 'Population unavailable';
  const numeric = Number(market.population);
  if (!Number.isFinite(numeric)) return 'Population unavailable';
  const year = market?.population_period ? String(market.population_period).slice(0, 4) : null;
  const geography = market?.population_geo_level === 'county' ? 'county proxy' : 'metro';
  return `${Math.round(numeric).toLocaleString('en-US')} people${year ? ` · ACS ${year}` : ''} · ${geography} estimate`;
}

export function getResearchMarketLatestRuleRatio(market) {
  return coerceFiniteNumber(market?.rent_to_price_ratio_latest);
}

export function getResearchMarketBestAvailableRuleRatio(market) {
  const candidates = [
    market?.best_available_rent_to_price_ratio,
    market?.rent_to_price_ratio_12m_avg,
    market?.rent_to_price_ratio_3m_avg,
    market?.rent_to_price_ratio_latest,
  ];
  for (const value of candidates) {
    const numeric = coerceFiniteNumber(value);
    if (numeric != null) return numeric;
  }
  return null;
}

export function getResearchMarketBestAvailableRuleBasis(market) {
  if (market?.best_available_rule_basis) return market.best_available_rule_basis;
  if (coerceFiniteNumber(market?.rent_to_price_ratio_12m_avg) != null) return '12m_avg';
  if (coerceFiniteNumber(market?.rent_to_price_ratio_3m_avg) != null) return '3m_avg';
  if (coerceFiniteNumber(market?.rent_to_price_ratio_latest) != null) return 'latest_month';
  return null;
}

export function compareResearchMarketRanking(left, right) {
  const leftRuleRatio = getResearchMarketLatestRuleRatio(left);
  const rightRuleRatio = getResearchMarketLatestRuleRatio(right);
  const nullable = compareNullable(leftRuleRatio, rightRuleRatio);
  if (nullable !== 0) return nullable;
  if (leftRuleRatio !== rightRuleRatio) {
    return Number(rightRuleRatio) - Number(leftRuleRatio);
  }
  return String(left.region_name || '').localeCompare(String(right.region_name || ''));
}

export function compareResearchMarketsByColumn(leftMarket, rightMarket, sortKey, sortDirection) {
  if (sortKey === '__ranking') return compareResearchMarketRanking(leftMarket, rightMarket);

  const left = sortKey === 'best_available_rent_to_price_ratio'
    ? getResearchMarketBestAvailableRuleRatio(leftMarket)
    : sortKey === 'rent_to_price_ratio_latest'
      ? getResearchMarketLatestRuleRatio(leftMarket)
      : sortKey === 'rent_prices'
        ? leftMarket?.rent_prices
        : leftMarket[sortKey];
  const right = sortKey === 'best_available_rent_to_price_ratio'
    ? getResearchMarketBestAvailableRuleRatio(rightMarket)
    : sortKey === 'rent_to_price_ratio_latest'
      ? getResearchMarketLatestRuleRatio(rightMarket)
      : sortKey === 'rent_prices'
        ? rightMarket?.rent_prices
        : rightMarket[sortKey];
  const nullable = compareNullable(left, right);
  if (nullable !== 0) return nullable;

  let result = 0;
  if (
    sortKey === 'region_name'
    || sortKey === 'state_name'
    || sortKey === 'state_name_full'
    || sortKey === 'snapshot_run_id'
    || sortKey === 'best_available_rule_basis'
  ) {
    result = String(left || '').localeCompare(String(right || ''));
  } else if (sortKey === 'month_end' || sortKey === 'snapshot_date') {
    result = String(left || '').localeCompare(String(right || ''));
  } else {
    result = Number(left) - Number(right);
  }

  if (result === 0) return compareResearchMarketRanking(leftMarket, rightMarket);
  return sortDirection === 'asc' ? result : -result;
}

export function filterResearchMarkets(markets, search) {
  const trimmedSearch = String(search || '').trim().toLowerCase();
  if (!trimmedSearch) return Array.isArray(markets) ? markets : [];
  return (Array.isArray(markets) ? markets : []).filter((market) => (
    String(market?.region_name || '').toLowerCase().includes(trimmedSearch)
  ));
}

export function sortResearchMarkets(markets, sortState = DEFAULT_RESEARCH_MARKET_SORT) {
  return [...(Array.isArray(markets) ? markets : [])].sort((left, right) => (
    compareResearchMarketsByColumn(left, right, sortState.key, sortState.direction)
  ));
}

export function rankResearchMarkets(markets) {
  return sortResearchMarkets(markets, DEFAULT_RESEARCH_MARKET_SORT).map((market, index) => ({
    ...market,
    rank: index + 1,
  }));
}

export function findResearchMarketBySlug(markets, regionSlug) {
  return (Array.isArray(markets) ? markets : []).find((market) => market?.region_slug === regionSlug) || null;
}
